import { HttpClient } from '@angular/common/http';
import { Injectable, computed, inject, signal } from '@angular/core';
import { Observable, catchError, map, of, switchMap, tap } from 'rxjs';
import {
  AccessRequestPayload,
  LoginResult,
  PlatformSession,
  TenantSession,
} from './auth.models';

const TENANT_KEY = 'et_tenant_session';
const PLATFORM_KEY = 'et_platform_session';
const PERMS_KEY = 'et_tenant_perms';
const CRITICAL_KEY = 'et_critical_cases';

function read<T>(key: string): T | null {
  const raw = localStorage.getItem(key);
  return raw ? (JSON.parse(raw) as T) : null;
}

/** Autenticação multi-tenant: guarda as sessões (tenant e plataforma) em signals + localStorage. */
@Injectable({ providedIn: 'root' })
export class AuthService {
  private readonly http = inject(HttpClient);

  readonly tenantSession = signal<TenantSession | null>(read<TenantSession>(TENANT_KEY));
  readonly platformSession = signal<PlatformSession | null>(read<PlatformSession>(PLATFORM_KEY));

  /** Permissões (recurso:ação) do utilizador do tenant, vindas do /me — usadas para adaptar a UI. */
  readonly permissions = signal<readonly string[]>(read<string[]>(PERMS_KEY) ?? []);

  /** O tenant tem a integração de casos críticos ligada (vem do /me). */
  readonly criticalCasesEnabled = signal<boolean>(read<boolean>(CRITICAL_KEY) ?? false);

  readonly currentTenant = computed(() => this.tenantSession()?.tenant ?? null);

  /** true se o utilizador tem a permissão informada. */
  can(permission: string): boolean {
    return this.permissions().includes(permission);
  }

  /** true se o utilizador tem QUALQUER uma das permissões (útil para exibir seções). */
  canAny(...perms: string[]): boolean {
    return perms.some((permission) => this.permissions().includes(permission));
  }

  /** Carrega as permissões do utilizador via /me (chamar após o bootstrap: login ou entrada no Layout). */
  /** Quando o /me foi pedido pela última vez: o login pede-o e o Layout, ao nascer, não o repete. */
  private permissionsLoadedAt = 0;

  loadPermissions(): void {
    // O login já o pediu há instantes e esperou por ele: repetir era um segundo /me idêntico.
    if (Date.now() - this.permissionsLoadedAt < 5000) return;
    this.fetchPermissions().subscribe();
  }

  /** Pede o /me e guarda as permissões; nunca falha (sem resposta ficam as que estavam em cache). */
  private fetchPermissions(): Observable<void> {
    const tenant = this.currentTenant();
    if (!tenant) return of(undefined);
    return this.http.get<{ permissions: string[]; criticalCasesEnabled?: boolean }>(`/api/t/${tenant}/me`).pipe(
      tap((me) => {
        this.permissionsLoadedAt = Date.now();
        this.storePermissions(me.permissions ?? []);
        const enabled = me.criticalCasesEnabled === true;
        this.criticalCasesEnabled.set(enabled);
        localStorage.setItem(CRITICAL_KEY, JSON.stringify(enabled));
      }),
      map(() => undefined),
      // mantém as permissões em cache; o backend ainda aplica 403
      catchError(() => of(undefined)),
    );
  }

  private storePermissions(perms: readonly string[]): void {
    this.permissions.set(perms);
    localStorage.setItem(PERMS_KEY, JSON.stringify(perms));
  }

  requestAccess(payload: AccessRequestPayload): Observable<{ id: number; status: string }> {
    return this.http.post<{ id: number; status: string }>('/api/access-requests', payload);
  }

  /**
   * Login único: o backend resolve pelo e-mail se é super-admin ou de que empresa é o
   * utilizador, e devolve o scope. Guardamos a sessão certa conforme esse scope.
   */
  login(email: string, password: string): Observable<LoginResult> {
    return this.http.post<LoginResult>('/api/auth/login', { email, password }).pipe(
      switchMap((result) => {
        const { token, name, email: userEmail, role } = result;
        if (result.scope === 'PLATFORM') {
          this.storePlatform({ token, name, email: userEmail, role });
          return of(result);
        }
        this.storeTenant({ token, name, email: userEmail, role, tenant: result.tenant ?? '' });
        // As permissões chegam antes de se navegar: as guardas das rotas decidem com elas, e com
        // as do utilizador anterior (ou nenhumas) mandavam o recém-chegado para a lista.
        return this.fetchPermissions().pipe(map(() => result));
      }),
    );
  }

  acceptInvite(tenant: string, token: string, password: string): Observable<void> {
    return this.http.post<void>(`/api/t/${tenant}/auth/accept-invite`, { token, password });
  }

  tenantToken(): string | null {
    return this.tenantSession()?.token ?? null;
  }

  platformToken(): string | null {
    return this.platformSession()?.token ?? null;
  }

  logoutTenant(): void {
    this.permissionsLoadedAt = 0;
    this.tenantSession.set(null);
    this.permissions.set([]);
    this.criticalCasesEnabled.set(false);
    localStorage.removeItem(CRITICAL_KEY);
    localStorage.removeItem(TENANT_KEY);
    localStorage.removeItem(PERMS_KEY);
  }

  logoutPlatform(): void {
    this.platformSession.set(null);
    localStorage.removeItem(PLATFORM_KEY);
  }

  private storeTenant(session: TenantSession): void {
    this.tenantSession.set(session);
    localStorage.setItem(TENANT_KEY, JSON.stringify(session));
  }

  private storePlatform(session: PlatformSession): void {
    this.platformSession.set(session);
    localStorage.setItem(PLATFORM_KEY, JSON.stringify(session));
  }
}
